import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConfigClient } from "../../clients/ConfigClient";
import { ActionKind } from "../../types/Action";
import { OperatorSubStateKind } from "../../types/Operator";
import { httpClient } from "../../utils/http";
import { Button } from "../Button";
import { Dialog } from "../Dialog";
import { RootStateKind, useRootAndSubState } from "../state/StateProvider";

const ipPattern =
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

const isValidIp = (ip: string): boolean => ipPattern.test(ip);

export const PaymentTerminalIpDialog = ({
    onClose,
    onIpUpdated
}: {
    onClose: () => void;
    onIpUpdated: () => void;
}) => {
    const { dispatch } = useRootAndSubState(
        RootStateKind.Operator,
        OperatorSubStateKind.Hardware
    );
    const { t } = useTranslation();

    const [currentIp, setCurrentIp] = useState<string | undefined>(undefined);
    const [ip, setIp] = useState("");

    const refreshCurrentIp = () =>
        new ConfigClient(httpClient)
            .systemInformation()
            .then((info) => setCurrentIp(info.paymentDeviceIp))
            .catch(() => setCurrentIp(undefined));

    useEffect(() => {
        refreshCurrentIp();
    }, []);

    const trimmedIp = ip.trim();
    const ipValid = trimmedIp.length === 0 || isValidIp(trimmedIp);

    const onUpdateIp = async () => {
        if (trimmedIp && isValidIp(trimmedIp)) {
            const succeeded = await dispatch({
                kind: ActionKind.UpdatePaymentDeviceIp,
                ip: trimmedIp
            });
            await refreshCurrentIp();
            onIpUpdated();

            if (succeeded) {
                onClose();
            }
        }
    };

    return (
        <Dialog
            dataTestId="payment-device-ip-dialog"
            title={t("operator.hardware.paymentDeviceConfig.dialogTitle")}
            onBackgroundClick={onClose}
            className="w-96"
        >
            <div className="flex w-full flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <div>
                        {t("operator.hardware.paymentDeviceConfig.currentIp")}:{" "}
                        <span className="font-semibold">
                            {currentIp || t("unknown")}
                        </span>
                    </div>
                    <label htmlFor="payment-device-ip">
                        {t("operator.hardware.paymentDeviceConfig.ipLabel")}
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            id="payment-device-ip"
                            data-testid="payment-device-ip-input"
                            className={`w-64 border-2 bg-white p-2 ${
                                trimmedIp.length === 0
                                    ? "border-gray-300"
                                    : ipValid
                                      ? "border-green-600"
                                      : "border-red-600"
                            }`}
                            value={ip}
                            onChange={(e) => setIp(e.target.value)}
                            type="text"
                        />
                        {!ipValid && (
                            <span
                                data-testid="payment-device-ip-error"
                                className="text-sm font-semibold text-red-600"
                            >
                                {t(
                                    "operator.hardware.paymentDeviceConfig.invalidIp"
                                )}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap gap-4">
                    <Button
                        variant="secondary"
                        data-testid="payment-device-set-ip"
                        disabled={!trimmedIp || !ipValid}
                        onClick={onUpdateIp}
                    >
                        {t("operator.hardware.paymentDeviceConfig.setIp")}
                    </Button>
                    <Button
                        variant="primary"
                        data-testid="payment-device-ip-dialog-close"
                        onClick={onClose}
                    >
                        {t("operator.hardware.paymentDeviceConfig.close")}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};
